import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { clearAuthExtras } from '../utils/permissoes';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  } catch {
    return true;
  }
};

export const clearAuthSession = () => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('userName');
  sessionStorage.removeItem('tokenZenTag');
  clearAuthExtras();
};

let interceptorsConfigured = false;
let onSessionLostHandler = null;

const isAuthEndpoint = (url = '') =>
  /\/account\/(login|revenda\/login)/i.test(url);

export const setupInterceptors = (navigate, onSessionLost) => {
  onSessionLostHandler = onSessionLost;

  if (interceptorsConfigured) return;
  interceptorsConfigured = true;

  api.interceptors.request.use(
    (config) => {
      const token = sessionStorage.getItem('token');

      if (token && isTokenExpired(token)) {
        clearAuthSession();
        onSessionLostHandler?.();
        navigate('/login', { replace: true });
        return Promise.reject(new Error('Token expirado, redirecionando para login'));
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  api.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || '';

      if (status === 401 && !isAuthEndpoint(url)) {
        clearAuthSession();
        onSessionLostHandler?.();
        navigate('/login', { replace: true });
      }

      return Promise.reject(error);
    }
  );
};

// Autenticação
export const login = (data) => api.post('/account/login', data);
export const loginRevenda = (data) => api.post('/account/revenda/login', data);

export const listarPerfisRevenda = (token) =>
  api.get('/perfis-revenda', { headers: { Authorization: `Bearer ${token}` } });

export const listarApiLogs = (token, params = {}) =>
  api.get('/api-logs', { params, headers: { Authorization: `Bearer ${token}` } });

// Clientes
export const cadastrarCliente = (data, token) => 
  api.post('/clientes', data, { headers: { Authorization: `Bearer ${token}` } });

export const listarClientes = (token) =>
  api.get('/clientes', { headers: { Authorization: `Bearer ${token}` } });

export const listarClientesPaginado = (params = {}, token) => {
  const query = new URLSearchParams();

  if (params.page) query.set("page", params.page);
  if (params.pageSize) query.set("pageSize", params.pageSize);
  if (params.search) query.set("search", params.search);
  if (params.sortBy) query.set("sortBy", params.sortBy);
  if (params.desc) query.set("desc", "true");
  if (params.revendaId) query.set("revendaId", params.revendaId);

  return api.get(`/clientes/licencas?${query.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const listarClientesTodos = async (token, opts = {}) => {
  const pageSize = opts.pageSize || 2000; 
  const sortBy = opts.sortBy || 'razaosocial';
  const desc = opts.desc || false;
  const search = opts.search || '';

  let page = 1;
  let all = [];
  let totalPages = 1;

  do {
    const res = await listarClientesPaginado(
      { page, pageSize, sortBy, desc, search },
      token
    );

    const data = res.data;
    const items = Array.isArray(data?.items) ? data.items : [];
    all = all.concat(items);

    totalPages = Number(data?.totalPages || 1);
    page += 1;
  } while (page <= totalPages);

  return all;
};

export const obterClientePorId = (id, token) => 
  api.get(`/clientes/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const editarCliente = (id, data, token) =>
  api.put(`/clientes/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const bloquearClienteZenGestao = (id, data, token) =>
  api.put(`/zengestao/bloquear-licenca/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const cancelarBloqueioProgramadoZenGestao = (id, token) =>
  api.put(`/zengestao/cancelar-bloqueio-programado/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const desbloquearClienteZenGestao = (id, token) =>
  api.put(`/zengestao/desbloquear-licenca/${id}`, null, { headers: { Authorization: `Bearer ${token}` } });

export const bonificarLicencaZenGestao = (id, data, token) =>
  api.put(`/zengestao/bonificar-licenca/${id}`, data || {}, { headers: { Authorization: `Bearer ${token}` } });

export const desbonificarLicencaZenGestao = (id, token) =>
  api.put(`/zengestao/desbonificar-licenca/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const cancelarLicencaZenGestao = (id, data, token) =>
  api.put(`/zengestao/cancelar-licenca/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const reativarLicencaZenGestao = (id, token) =>
  api.put(`/zengestao/reativar-licenca/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const bloquearClienteZenMobile = (id, data, token) =>
  api.put(`/zenmobile/bloquear-licenca/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const desbloquearClienteZenMobile = (id, token) =>
  api.put(`/zenmobile/desbloquear-licenca/${id}`, null, { headers: { Authorization: `Bearer ${token}` } });

export const bonificarLicencaZenMobile = (id, data, token) =>
  api.put(`/zenmobile/bonificar-licenca/${id}`, data || {}, { headers: { Authorization: `Bearer ${token}` } });

export const desbonificarLicencaZenMobile = (id, token) =>
  api.put(`/zenmobile/desbonificar-licenca/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const cancelarLicencaZenMobile = (id, data, token) =>
  api.put(`/zenmobile/cancelar-licenca/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const reativarLicencaZenMobile = (id, token) =>
  api.put(`/zenmobile/reativar-licenca/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const bonificarLicencaZenPDV = (id, data, token) =>
  api.put(`/pdvs/bonificar-licenca/${id}`, data || {}, { headers: { Authorization: `Bearer ${token}` } });

export const desbonificarLicencaZenPDV = (id, token) =>
  api.put(`/pdvs/desbonificar-licenca/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const cancelarLicencaZenPDV = (id, data, token) =>
  api.put(`/pdvs/cancelar-licenca/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const reativarLicencaZenPDV = (id, token) =>
  api.put(`/pdvs/reativar-licenca/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const bonificarLicencaZenTag = (id, data, token) =>
  api.put(`/zentag/bonificar-licenca/${id}`, data || {}, { headers: { Authorization: `Bearer ${token}` } });

export const desbonificarLicencaZenTag = (id, token) =>
  api.put(`/zentag/desbonificar-licenca/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const cancelarLicencaZenTag = (id, data, token) =>
  api.put(`/zentag/cancelar-licenca/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const reativarLicencaZenTag = (id, token) =>
  api.put(`/zentag/reativar-licenca/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const excluirCliente = (id, token) =>
  api.delete(`/clientes/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const listarClientesZenMobile = (token, params = {}) => {
  const query = new URLSearchParams();

  if (params.page) query.set("page", params.page);
  if (params.pageSize) query.set("pageSize", params.pageSize);
  if (params.search) query.set("search", params.search);
  if (params.sortBy) query.set("sortBy", params.sortBy);
  if (params.desc) query.set("desc", "true");
  if (params.revendaId !== undefined && params.revendaId !== null) query.set("revendaId", params.revendaId);
  if (params.ativo !== undefined && params.ativo !== null) query.set("ativo", params.ativo);
  if (params.cancelada !== undefined && params.cancelada !== null) query.set("cancelada", params.cancelada);

  const qs = query.toString();
  return api.get(`/zenmobile${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const cadastrarClienteZenGestao = (data, token) =>
  api.post('/zengestao', data, { headers: { Authorization: `Bearer ${token}` } });

export const listarClientesZenGestao = (token, params = {}) => {
  const query = new URLSearchParams();

  if (params.page) query.set("page", params.page);
  if (params.pageSize) query.set("pageSize", params.pageSize);
  if (params.search) query.set("search", params.search);
  if (params.sortBy) query.set("sortBy", params.sortBy);
  if (params.desc) query.set("desc", "true");
  if (params.revendaId !== undefined && params.revendaId !== null) query.set("revendaId", params.revendaId);
  if (params.ativo !== undefined && params.ativo !== null) query.set("ativo", params.ativo);
  if (params.cancelada !== undefined && params.cancelada !== null) query.set("cancelada", params.cancelada);

  const qs = query.toString();
  return api.get(`/zengestao${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const cadastrarClienteZenMobile = (data, token) =>
  api.post('/zenmobile', data, { headers: { Authorization: `Bearer ${token}` } });

export const obterClienteZenMobile = (id, token) =>
    api.get(`/zenmobile/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const atualizarClienteZenMobile = (id, data, token) =>
    api.put(`/zenmobile/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const obterClienteZenGestao = (id, token) =>
    api.get(`/zengestao/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const atualizarClienteZenGestao = (id, data, token) =>
    api.put(`/zengestao/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

// Módulos de licenciamento
export const listarModulos = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.produto) q.set("produto", params.produto);
  if (params.tipo) q.set("tipo", params.tipo);
  if (params.ativo !== undefined && params.ativo !== null) q.set("ativo", params.ativo);
  const qs = q.toString();
  return api.get(`/modulos${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const obterModulosLicenca = (licencaTipo, licencaId, token) =>
  api.get(`/licenca-modulos/${licencaTipo}/${licencaId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const atualizarModulosLicenca = (licencaTipo, licencaId, itens, token) =>
  api.put(
    `/licenca-modulos/${licencaTipo}/${licencaId}`,
    { itens },
    { headers: { Authorization: `Bearer ${token}` } }
  );

export const listarZenGestaoEmpresas = (zenGestaoId, token) =>
  api.get(`/zengestao/${zenGestaoId}/empresas`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const criarZenGestaoEmpresa = (zenGestaoId, data, token) =>
  api.post(`/zengestao/${zenGestaoId}/empresas`, data, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const inativarZenGestaoEmpresa = (zenGestaoId, empresaId, token) =>
  api.delete(`/zengestao/${zenGestaoId}/empresas/${empresaId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const atualizarZenGestaoEmpresa = (zenGestaoId, empresaId, data, token) =>
  api.put(`/zengestao/${zenGestaoId}/empresas/${empresaId}`, data, {
    headers: { Authorization: `Bearer ${token}` },
  });

export const excluirZenGestaoEmpresa = inativarZenGestaoEmpresa;

// Relatórios de licenciamento
export const relatorioLicencasAtivas = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.revendaId) q.set("revendaId", params.revendaId);
  if (params.produto) q.set("produto", params.produto);
  const qs = q.toString();
  return api.get(`/relatorios/licencas-ativas${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const relatorioMovimentacoes = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.revendaId) q.set("revendaId", params.revendaId);
  if (params.produto) q.set("produto", params.produto);
  if (params.tipo) q.set("tipo", params.tipo);
  if (params.de) q.set("de", params.de);
  if (params.ate) q.set("ate", params.ate);
  const qs = q.toString();
  return api.get(`/relatorios/movimentacoes${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const relatorioFaturamentoRevenda = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.revendaId) q.set("revendaId", params.revendaId);
  if (params.ano) q.set("ano", params.ano);
  if (params.mes) q.set("mes", params.mes);
  if (params.aplicarCarencia != null) q.set("aplicarCarencia", params.aplicarCarencia);
  const qs = q.toString();
  return api.get(`/relatorios/faturamento-revenda${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const relatorioFaturamentoComposicao = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.distribuidorId) q.set("distribuidorId", params.distribuidorId);
  if (params.revendaId) q.set("revendaId", params.revendaId);
  if (params.ano) q.set("ano", params.ano);
  if (params.mes) q.set("mes", params.mes);
  if (params.aplicarCarencia != null) q.set("aplicarCarencia", params.aplicarCarencia);
  const qs = q.toString();
  return api.get(`/relatorios/faturamento-composicao${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const listarDistribuidores = (token) =>
  api.get("/distribuidores", { headers: { Authorization: `Bearer ${token}` } });

export const listarDistribuidoresDropdown = (token) =>
  api.get("/distribuidores/dropdown", { headers: { Authorization: `Bearer ${token}` } });

export const obterDistribuidor = (id, token) =>
  api.get(`/distribuidores/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const cadastrarDistribuidor = (data, token) =>
  api.post("/distribuidores", data, { headers: { Authorization: `Bearer ${token}` } });

export const atualizarDistribuidor = (id, data, token) =>
  api.put(`/distribuidores/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const bloquearDistribuidor = (id, token) =>
  api.put(`/distribuidores/${id}/bloquear`, null, { headers: { Authorization: `Bearer ${token}` } });

export const desbloquearDistribuidor = (id, token) =>
  api.put(`/distribuidores/${id}/desbloquear`, null, { headers: { Authorization: `Bearer ${token}` } });

export const listarUsuariosDistribuidorAdmin = (token, params = {}) =>
  api.get("/distribuidores/usuarios", { params, headers: { Authorization: `Bearer ${token}` } });

export const obterUsuarioDistribuidor = (id, token) =>
  api.get(`/distribuidores/usuarios/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const cadastrarUsuarioDistribuidor = (distribuidorId, data, token) =>
  api.post(`/distribuidores/${distribuidorId}/usuarios`, data, { headers: { Authorization: `Bearer ${token}` } });

export const listarUsuariosDoDistribuidor = (id, token) =>
  api.get(`/distribuidores/${id}/usuarios`, { headers: { Authorization: `Bearer ${token}` } });

export const atualizarUsuarioDoDistribuidor = (distribuidorId, usuarioId, data, token) =>
  api.put(`/distribuidores/${distribuidorId}/usuarios/${usuarioId}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const desativarUsuarioDistribuidor = (distribuidorId, usuarioId, token) =>
  api.put(`/distribuidores/${distribuidorId}/usuarios/${usuarioId}/desativar`, null, { headers: { Authorization: `Bearer ${token}` } });

export const ativarUsuarioDistribuidor = (distribuidorId, usuarioId, token) =>
  api.put(`/distribuidores/${distribuidorId}/usuarios/${usuarioId}/ativar`, null, { headers: { Authorization: `Bearer ${token}` } });

export const excluirUsuarioDistribuidor = (distribuidorId, usuarioId, token) =>
  api.delete(`/distribuidores/${distribuidorId}/usuarios/${usuarioId}`, { headers: { Authorization: `Bearer ${token}` } });

export const relatorioLicencasCanceladas = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.revendaId) q.set("revendaId", params.revendaId);
  if (params.produto) q.set("produto", params.produto);
  if (params.de) q.set("de", params.de);
  if (params.ate) q.set("ate", params.ate);
  const qs = q.toString();
  return api.get(`/relatorios/licencas-canceladas${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

// Tabelas de preço
export const listarTabelasPreco = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.ativo !== undefined && params.ativo !== null) q.set("ativo", params.ativo);
  const qs = q.toString();
  return api.get(`/tabelas-preco${qs ? `?${qs}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const obterTabelaPreco = (id, token) =>
  api.get(`/tabelas-preco/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const criarTabelaPreco = (data, token) =>
  api.post("/tabelas-preco", data, { headers: { Authorization: `Bearer ${token}` } });

export const atualizarTabelaPreco = (id, data, token) =>
  api.put(`/tabelas-preco/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const excluirTabelaPreco = (id, token) =>
  api.delete(`/tabelas-preco/${id}`, { headers: { Authorization: `Bearer ${token}` } });

// PDVs
export const listarPdvs = (token, params = {}) => {
  const q = new URLSearchParams();
  if (params.search) q.set("search", params.search);
  if (params.revendaId) q.set("revendaId", params.revendaId);
  if (params.ativo !== undefined && params.ativo !== null) q.set("ativo", params.ativo);
  if (params.cancelada !== undefined && params.cancelada !== null) q.set("cancelada", params.cancelada);
  return api.get(`/pdvs?${q.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
};


export const gerarLicenca = (id, data, token) =>
  api.put(`/pdvs/gerar-chave/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const baixarChave = (id, token) =>
  api.get(`/pdvs/${id}/baixar-chave`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });

export const cadastrarPdv = (data, token) => 
  api.post('/pdvs', data, { headers: { Authorization: `Bearer ${token}` } });

export const excluirPdv = (id, token) =>
  api.delete(`/pdvs/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const obterPdvPorId = (id, token) =>
  api.get(`/pdvs/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const editarPdv = (id, data, token) =>
  api.put(`/pdvs/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const bloquearLicencaPdv = (id, data, token) =>
  api.put(`/pdvs/bloquear-licenca/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const desbloquearLicencaPdv = (id, token) =>
  api.put(`/pdvs/desbloquear-licenca/${id}`, null, { headers: { Authorization: `Bearer ${token}` } });


// Usuários
export const listarUsuarios = (token) =>
  api.get('/system/usuarios', { headers: { Authorization: `Bearer ${token}` } });

export const obterUsuarioPorId = (id, token) =>
  api.get(`/system/usuarios/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const cadastrarUsuario = (data, token) =>
  api.post('/system/usuarios', data, { headers: { Authorization: `Bearer ${token}` } });

export const editarUsuario = (id, data, token) =>
  api.put(`/system/usuarios/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const excluirUsuario = (id, token) =>
  api.delete(`/system/usuarios/${id}`, { headers: { Authorization: `Bearer ${token}` } });

// Home
export const getQuantidadeClientes = (token) => {
  return api.get('/clientes/quantidade', { headers: { Authorization: `Bearer ${token}` } });
};

export const getQuantidadePdvs = (token) => {
  return api.get('/pdvs/quantidade', { headers: { Authorization: `Bearer ${token}` } });
};

export const getQuantidadeChavesBloqueadas = (token) => {
  return api.get('/pdvs/quantidade-chaves-bloqueadas', { headers: { Authorization: `Bearer ${token}` } });
};

export const getQuantidadeChavesAtualizadas = (token) => {
  return api.get('/pdvs/quantidade-chaves-atualizadas', { headers: { Authorization: `Bearer ${token}` } });
};

// Revendas
export const listarRevendasPaginado = (params = {}, token) => {
  const query = new URLSearchParams();

  if (params.page) query.set("page", params.page);
  if (params.pageSize) query.set("pageSize", params.pageSize);
  if (params.search) query.set("search", params.search);
  if (params.sortBy) query.set("sortBy", params.sortBy);
  if (params.desc) query.set("desc", "true");

  return api.get(`/revendas?${query.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
};

export const listarRevendasDropdown = (token) =>
  api.get("/revendas", {
    params: { page: 1, pageSize: 200, sortBy: "razaosocial", desc: false },
    headers: { Authorization: `Bearer ${token}` },
  });


export const excluirRevenda = (id, token) =>
  api.delete(`/revendas/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const listarRevendas = (token) =>
  api.get('/revendas', { headers: { Authorization: `Bearer ${token}` } });

export const cadastrarRevenda = (data, token) =>
  api.post('/revendas', data, { headers: { Authorization: `Bearer ${token}` } });

export const obterRevendaPorId = (id, token) =>
  api.get(`/revendas/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const editarRevenda = (id, data, token) =>
  api.put(`/revendas/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const bloquearRevenda = (id, token) =>
  api.put(`/revendas/${id}/bloquear`, null, { headers: { Authorization: `Bearer ${token}` } });

export const desbloquearRevenda = (id, token) =>
  api.put(`/revendas/${id}/desbloquear`, null, { headers: { Authorization: `Bearer ${token}` } });


// zentag
export const listarClientesZenTag = (token, params) =>
  api.get("/zentag", {
    params,
    headers: { Authorization: `Bearer ${token}` },
  });

export const obterClienteZenTagPorId = (id, token) =>
  api.get(`/zentag/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const ativarClienteZenTag = (data, token) =>
  api.post("/zentag/ativar", data, { headers: { Authorization: `Bearer ${token}` } });

export const bloquearClienteZenTag = (id, data, token) =>
  api.put(`/zentag/bloquear/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const desbloquearClienteZenTag = (id, token) =>
  api.put(`/zentag/desbloquear/${id}`, null, { headers: { Authorization: `Bearer ${token}` } });

export const editarClienteZenTag = (id, data, token) =>
  api.put(`/zentag/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const excluirClienteZenTag = (id, token) =>
  api.delete(`/zentag/${id}`, { headers: { Authorization: `Bearer ${token}` } });

// zentag - autenticador (get or create)
export const obterOuCriarAutenticadorZenTag = (licId, token) =>
  api.get(`/zentag/${licId}/autenticador`, { headers: { Authorization: `Bearer ${token}` } });

// zentag - autenticador (regerar)
export const regerarAutenticadorZenTag = (licId, token) =>
  api.put(`/zentag/${licId}/autenticador/regerar`, null, { headers: { Authorization: `Bearer ${token}` } });

// zentag - usuarios (CRUD básico)
export const listarUsuariosZenTagDoCliente = (licId, token) =>
  api.get(`/zentag/${licId}/usuarios`, { headers: { Authorization: `Bearer ${token}` } });

export const criarUsuarioZenTagDoCliente = (licId, data, token) =>
  api.post(`/zentag/${licId}/usuarios`, data, { headers: { Authorization: `Bearer ${token}` } });

export const atualizarUsuarioZenTagDoCliente = (licId, usuarioId, data, token) =>
  api.put(`/zentag/${licId}/usuarios/${usuarioId}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const listarClientesZenPass = (token, params) =>
  api.get("/zenpass", { params, headers: { Authorization: `Bearer ${token}` } });

export const obterClienteZenPassPorId = (id, token) =>
  api.get(`/zenpass/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const ativarClienteZenPass = (data, token) =>
  api.post("/zenpass/ativar", data, { headers: { Authorization: `Bearer ${token}` } });

export const editarClienteZenPass = (id, data, token) =>
  api.put(`/zenpass/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const bloquearClienteZenPass = (id, data, token) =>
  api.put(`/zenpass/bloquear/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const desbloquearClienteZenPass = (id, token) =>
  api.put(`/zenpass/desbloquear/${id}`, null, { headers: { Authorization: `Bearer ${token}` } });

export const bonificarLicencaZenPass = (id, data, token) =>
  api.put(`/zenpass/bonificar-licenca/${id}`, data || {}, { headers: { Authorization: `Bearer ${token}` } });

export const desbonificarLicencaZenPass = (id, token) =>
  api.put(`/zenpass/desbonificar-licenca/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const cancelarLicencaZenPass = (id, data, token) =>
  api.put(`/zenpass/cancelar-licenca/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const reativarLicencaZenPass = (id, token) =>
  api.put(`/zenpass/reativar-licenca/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });

export const listarUsuariosZenPass = (licId, token) =>
  api.get(`/zenpass/${licId}/usuarios`, { headers: { Authorization: `Bearer ${token}` } });

export const criarUsuarioZenPass = (licId, data, token) =>
  api.post(`/zenpass/${licId}/usuarios`, data, { headers: { Authorization: `Bearer ${token}` } });

export const atualizarUsuarioZenPass = (licId, usuarioId, data, token) =>
  api.put(`/zenpass/${licId}/usuarios/${usuarioId}`, data, { headers: { Authorization: `Bearer ${token}` } });

// Revenda Usuários
// GET /v1/revendas/usuarios?revendaId=123
export const listarUsuariosRevenda = (token, params) =>
  api.get("/revendas/usuarios", { params, headers: { Authorization: `Bearer ${token}` } });

// GET /v1/revendas/usuarios/{id}
export const obterUsuarioRevendaPorId = (id, token) =>
  api.get(`/revendas/usuarios/${id}`, { headers: { Authorization: `Bearer ${token}` } });

// POST /v1/revendas/usuarios
export const cadastrarUsuarioRevenda = (data, token) =>
  api.post("/revendas/usuarios", data, { headers: { Authorization: `Bearer ${token}` } });

// PUT /v1/revendas/usuarios/{id}
export const editarUsuarioRevenda = (id, data, token) =>
  api.put(`/revendas/usuarios/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

// DELETE /v1/revendas/usuarios/{id}
export const excluirUsuarioRevenda = (id, token) =>
  api.delete(`/revendas/usuarios/${id}`, { headers: { Authorization: `Bearer ${token}` } });

// PUT /v1/revendas/usuarios/{id}/desativar
export const desativarUsuarioRevenda = (id, token) =>
  api.put(`/revendas/usuarios/${id}/desativar`, {}, { headers: { Authorization: `Bearer ${token}` } });

// PUT /v1/revendas/usuarios/{id}/ativar
export const ativarUsuarioRevenda = (id, token) =>
  api.put(`/revendas/usuarios/${id}/ativar`, {}, { headers: { Authorization: `Bearer ${token}` } });

// Dashboard v1
export const getDashboardOverview = (token, params) =>
  api.get("/dashboard/overview", {
    params,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getDashboardTopSystems = (token, params) =>
  api.get("/dashboard/top-systems", {
    params,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getDashboardTopRevendas = (token, params) =>
  api.get("/dashboard/top-revendas", {
    params,
    headers: { Authorization: `Bearer ${token}` },
  });

export const getDashboardTimeline = (token, params) =>
  api.get("/dashboard/timeline", {
    params,
    headers: { Authorization: `Bearer ${token}` },
  });

// Vendedores
export const cadastrarVendedor = (data, token) =>
  api.post('/vendedores', data, { headers: { Authorization: `Bearer ${token}` } });

export const listarVendedoresPaginado = (params = {}, token) => {
  const query = new URLSearchParams();

  if (params.page) query.set("page", params.page);
  if (params.pageSize) query.set("pageSize", params.pageSize);
  if (params.search) query.set("search", params.search);
  if (params.sortBy) query.set("sortBy", params.sortBy);
  if (params.desc) query.set("desc", "true");
  if (params.revendaId) query.set("revendaId", params.revendaId);

  return api.get(`/vendedores?${query.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const listarVendedoresTodos = async (token, opts = {}) => {
  const pageSize = opts.pageSize || 2000;
  const sortBy = opts.sortBy || 'nome';
  const desc = opts.desc || false;
  const search = opts.search || '';
  const revendaId = opts.revendaId; // pode ser 0

  let page = 1;
  let all = [];
  let totalPages = 1;

  do {
    const res = await listarVendedoresPaginado(
      { page, pageSize, sortBy, desc, search, revendaId },
      token
    );

    const data = res.data;
    const items = Array.isArray(data?.items) ? data.items : [];
    all = all.concat(items);

    totalPages = Number(data?.totalPages || 1);
    page += 1;
  } while (page <= totalPages);

  return all;
};


export const obterVendedorPorId = (id, token) =>
  api.get(`/vendedores/${id}`, { headers: { Authorization: `Bearer ${token}` } });

export const editarVendedor = (id, data, token) =>
  api.put(`/vendedores/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });

export const excluirVendedor = (id, token) =>
  api.delete(`/vendedores/${id}`, { headers: { Authorization: `Bearer ${token}` } });

//Certificado

export const uploadCertificado = (file, password, token) => {
  const formData = new FormData();
  formData.append("certificado", file);
  formData.append("senha", password);

  return api.post("/certificados/upload", formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "multipart/form-data",
    },
  });
};


export default api;
